// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Pure layout mathematics. No DOM. Every function here has positional tests in
// tests/layout.test.ts — in-bounds, no NaN, no pairwise overlap, and totals
// conserved. Area-only tests pass on visually broken layouts, so they are not
// enough on their own.

export interface Rect { x: number; y: number; w: number; h: number }

// ---------------------------------------------------------------------------
// Histogram
// ---------------------------------------------------------------------------

export interface Bin { lo: number; hi: number; count: number; x: number; w: number; items: number[] }

/**
 * Fixed-width bins across [lo, hi]. Bins are half-open [lo, hi) except the last,
 * which includes its upper bound so the maximum value is never dropped.
 */
export function histogram(values: number[], binWidth: number, width: number, lo?: number, hi?: number): Bin[] {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length || !(binWidth > 0) || !(width > 0)) return [];
  const min = lo ?? Math.min(...finite);
  const max = hi ?? Math.max(...finite);
  const start = Math.floor(min / binWidth) * binWidth;
  const end = Math.ceil((max + (max === start ? binWidth : 0)) / binWidth) * binWidth;
  const n = Math.max(1, Math.round((end - start) / binWidth));
  const bins: Bin[] = Array.from({ length: n }, (_, i) => ({
    lo: start + i * binWidth,
    hi: start + (i + 1) * binWidth,
    count: 0,
    x: (i / n) * width,
    w: width / n,
    items: [],
  }));
  for (const v of finite) {
    let idx = Math.floor((v - start) / binWidth);
    if (idx >= n) idx = n - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
    bins[idx].items.push(v);
  }
  return bins;
}

// ---------------------------------------------------------------------------
// Sankey
// ---------------------------------------------------------------------------

export interface SankeyFlow { source: string; target: string; value: number }
export interface SankeyNode { id: string; side: 'source' | 'target'; value: number; x: number; y: number; w: number; h: number }
export interface SankeyLink { source: string; target: string; value: number; y0: number; y1: number; thickness: number }
export interface SankeyLayout { nodes: SankeyNode[]; links: SankeyLink[]; scale: number }

/**
 * Two-column Sankey. Node heights and link thicknesses are proportional to
 * value; within a column, nodes are stacked in descending value with a fixed
 * gap, so no two nodes on the same side can overlap.
 */
export function sankey(
  flows: SankeyFlow[],
  width: number,
  height: number,
  opts: { nodeWidth?: number; gap?: number } = {},
): SankeyLayout {
  const nodeWidth = opts.nodeWidth ?? 14;
  const gap = opts.gap ?? 6;
  const usable = flows.filter((f) => Number.isFinite(f.value) && f.value > 0);
  if (!usable.length || !(width > 0) || !(height > 0)) return { nodes: [], links: [], scale: 0 };

  const totals = { source: new Map<string, number>(), target: new Map<string, number>() };
  for (const f of usable) {
    totals.source.set(f.source, (totals.source.get(f.source) ?? 0) + f.value);
    totals.target.set(f.target, (totals.target.get(f.target) ?? 0) + f.value);
  }
  const order = (m: Map<string, number>) => [...m].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const srcOrder = order(totals.source);
  const tgtOrder = order(totals.target);

  const sumOf = (o: [string, number][]) => o.reduce((a, b) => a + b[1], 0);
  const biggestSum = Math.max(sumOf(srcOrder), sumOf(tgtOrder));
  const maxCount = Math.max(srcOrder.length, tgtOrder.length);
  const available = Math.max(1, height - gap * Math.max(0, maxCount - 1));
  const scale = available / biggestSum;

  const nodes: SankeyNode[] = [];
  const place = (o: [string, number][], side: 'source' | 'target', x: number) => {
    let y = 0;
    for (const [id, value] of o) {
      const h = value * scale;
      nodes.push({ id, side, value, x, y, w: nodeWidth, h });
      y += h + gap;
    }
  };
  place(srcOrder, 'source', 0);
  place(tgtOrder, 'target', Math.max(nodeWidth, width - nodeWidth));

  const byId = new Map(nodes.map((n) => [`${n.side}:${n.id}`, n]));
  const cursor = new Map<string, number>();
  const links: SankeyLink[] = [];
  const sorted = [...usable].sort((a, b) => b.value - a.value || a.source.localeCompare(b.source));
  for (const f of sorted) {
    const sn = byId.get(`source:${f.source}`)!;
    const tn = byId.get(`target:${f.target}`)!;
    const sk = `source:${f.source}`;
    const tk = `target:${f.target}`;
    const sy = sn.y + (cursor.get(sk) ?? 0);
    const ty = tn.y + (cursor.get(tk) ?? 0);
    const thickness = f.value * scale;
    cursor.set(sk, (cursor.get(sk) ?? 0) + thickness);
    cursor.set(tk, (cursor.get(tk) ?? 0) + thickness);
    links.push({ source: f.source, target: f.target, value: f.value, y0: sy + thickness / 2, y1: ty + thickness / 2, thickness });
  }
  return { nodes, links, scale };
}

/** Cubic Bézier ribbon between the two node columns. */
export function sankeyPath(x0: number, y0: number, x1: number, y1: number): string {
  const mid = (x0 + x1) / 2;
  return `M${x0.toFixed(1)} ${y0.toFixed(1)}C${mid.toFixed(1)} ${y0.toFixed(1)} ${mid.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
}

// ---------------------------------------------------------------------------
// Regime ribbon
// ---------------------------------------------------------------------------

export interface RibbonCell extends Rect { row: number; col: number }

/**
 * A grid of equal cells, `cols` across and `rows` down, exactly filling the box.
 * Used for the regime ribbon (years × days) and the brand × region matrix.
 * Cells are laid out on exact fractional boundaries so adjacent cells are flush
 * and never overlap, however awkward the division.
 */
export function gridCells(cols: number, rows: number, width: number, height: number, pad = 0): RibbonCell[] {
  const out: RibbonCell[] = [];
  if (cols <= 0 || rows <= 0 || !(width > 0) || !(height > 0)) return out;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = (c / cols) * width;
      const x1 = ((c + 1) / cols) * width;
      const y0 = (r / rows) * height;
      const y1 = ((r + 1) / rows) * height;
      out.push({
        row: r,
        col: c,
        x: x0 + pad / 2,
        y: y0 + pad / 2,
        w: Math.max(0, x1 - x0 - pad),
        h: Math.max(0, y1 - y0 - pad),
      });
    }
  }
  return out;
}
